import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { useLanguage } from "@/context/LanguageContext";
import { PRIVACY } from "@/lib/privacyContent";
import LanguageSwitcher from "@/components/landing/LanguageSwitcher";
import { useCanonical } from "@/lib/useCanonical";

const sanitize = (html) =>
  DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["a", "strong", "em", "b", "i", "br", "span"],
    ALLOWED_ATTR: ["href", "target", "rel", "class"],
  });

export default function Privacy() {
  const { lang } = useLanguage();
  const t = PRIVACY[lang] || PRIVACY.pt;
  useCanonical("/privacidade");

  useEffect(() => {
    document.title = `${t.title} · Na Ponta Da Língua Brasil`;
    window.scrollTo(0, 0);
  }, [t.title, lang]);

  return (
    <div className="bg-[#f4f1ea] min-h-screen text-[#1e2a5a]">
      <header className="border-b border-[#e5e3db]">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
          <Link
            to="/"
            data-testid="privacy-back-home"
            className="text-sm text-[#5c5c5c] hover:text-[#1e2a5a] transition-colors"
          >
            {t.backHome}
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-14 sm:py-20">
        <p className="text-xs uppercase tracking-[0.22em] text-[#d9381e]">
          {t.overline}
        </p>
        <h1
          data-testid="privacy-title"
          className="font-display mt-4 text-4xl sm:text-5xl leading-[1.05] tracking-tight"
        >
          {t.title}
        </h1>
        <p className="mt-4 text-xs uppercase tracking-[0.18em] text-[#9a9a9a]">
          {t.lastUpdate}
        </p>
        <p className="mt-8 text-base sm:text-lg leading-relaxed text-[#3a3a3a]">
          {t.intro}
        </p>

        <div className="mt-12 space-y-12">
          {t.sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-display text-xl sm:text-2xl text-[#1e2a5a] leading-snug">
                {s.h}
              </h2>
              {s.p?.map((para, j) => (
                <p
                  key={j}
                  className="mt-3 text-[15px] leading-relaxed text-[#3a3a3a]"
                  dangerouslySetInnerHTML={{ __html: sanitize(para) }}
                />
              ))}
              {s.ul && (
                <ul className="mt-3 space-y-2 text-[15px] leading-relaxed text-[#3a3a3a]">
                  {s.ul.map((item, k) => (
                    <li
                      key={k}
                      className="pl-5 relative before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-1.5 before:h-1.5 before:bg-[#d9381e] before:rounded-full"
                      dangerouslySetInnerHTML={{ __html: sanitize(item) }}
                    />
                  ))}
                </ul>
              )}
              {s.pAfter && (
                <p
                  className="mt-4 text-[15px] leading-relaxed text-[#3a3a3a] italic"
                  dangerouslySetInnerHTML={{ __html: sanitize(s.pAfter) }}
                />
              )}
            </section>
          ))}
        </div>

        <div className="mt-20 border-t border-[#e5e3db] pt-8 text-sm text-[#5c5c5c]">
          <Link to="/" className="link-underline">
            {t.backHome}
          </Link>
        </div>
      </main>
    </div>
  );
}
